/* eslint-disable react/prop-types */
/**
 * CartProvider
 * Contain most logic handing with Omnitech Api
 */

import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { createPersistedState } from '../../cache'
import getErrorMessageByCode from '../../omnitech-error/get-error-message-by-code'
import DineInCartContext from './dine-in-cart-context'
import { createCart, fetchCart, updateCart, checkout } from '../../cart/shared/cart-api'
const useCartIdState = createPersistedState('dineInCartId')
const useStagingCartState = createPersistedState('stagingCart')
const useInventoryStoreCodeState = createPersistedState('dineInInventoryStoreCode')
const useInventoryStoreIdState = createPersistedState('dineInInventoryStoreId')

export default function CartProvider({ children }) {
  const DEFAULT_STAGING_CART = {actions: [], totalItems: 0}

  const [dineInCartId, setDineInCartId] = useCartIdState('')
  const [cart, setCart] = useState({})
  const [stagingCart, setStagingCart] = useStagingCartState(DEFAULT_STAGING_CART)
  const [inventoryStoreCode, setInventoryStoreCode] = useInventoryStoreCodeState('')
  const [inventoryStoreId, setInventoryStoreId] = useInventoryStoreIdState(null)
  const [totalItems, setTotalItems] = useState(0)
  const [initCart, setInitCart] = useState({})
  // const [totalStagingItems, setTotalStagingItems] = useState(0)

  const mergeCartActions = (previousActions, currentActions) => {
    const actionsToRemove = _.filter(currentActions, ({quantity}) => quantity === 0)
    const actionsToAdd = _.filter(currentActions, ({quantity}) => quantity > 0)
    const uuidsToRemove = _.keys(_.groupBy(actionsToRemove, 'groupUuid'))
    return [
      ..._.reject(previousActions, ({groupUuid, identifierUuid}) => _.includes(uuidsToRemove, groupUuid) || _.includes(_.map(currentActions, 'identifierUuid'), identifierUuid)),
      ...actionsToAdd
    ]
  }

  function getMainLineProperties(lines) {
    return _.sumBy(_.filter(lines, 'isMainProperty'), 'quantity')
  }

  function DineInCartIdMissingException() {
    return {
      generalError: {
        code: 404,
        message: getErrorMessageByCode(404)
      }
    }
  }

  function handleInitCartForCartBadge({ includes = [], ...params } = {}) {
    const options = {
      params: {
        includes: _.uniq(['cart_line_properties', ...includes]),
        ...params
      }
    }
    if (
      !_.isEmpty(dineInCartId) &&
      _.isString(dineInCartId)
    ) {
      return fetchCart({ cartId: dineInCartId, ...options })
        .then(({ cart }) => {
          setTotalItems(getMainLineProperties(_.get(cart, 'cartLineProperties', [])))
          setInitCart(cart)
          setInventoryStoreCode(_.get(cart, 'cartShipments.0.inventoryStore.code', ''))
          setInventoryStoreId(_.get(cart, 'cartShipments.0.inventoryStore.id', null))
        })
    } else {
      throw new DineInCartIdMissingException()
    }
  }

  // async function createCartIfNotExist(error) {
  //   const generalError = _.get(error, 'generalError', {})
  //   if (generalError.code === 404) {
  //     await handleCreateCart({ params: {} })
  //   }
  // }

  function handleRefreshCart(options) {
    if (_.isEmpty(cart)) {
      if (!_.isUndefined(dineInCartId) && !_.isNull(dineInCartId) && !_.isEmpty(dineInCartId) && !_.isEqual(dineInCartId, 'null')) {
        return handleFetchCart(options)
      } else {
        return handleCreateCart(options)
      }
    } else {
      return handleFetchCart(options)
    }
  }

  function handleResetCart() {
    setDineInCartId('')
    setInventoryStoreCode('')
    setInventoryStoreId(null)
    setCart({})
    setTotalItems(0)
    setInitCart({})
  }
  function handleResetStagingCart() {
    setStagingCart(DEFAULT_STAGING_CART)
  }

  async function handleCheckout(options) {
    try {
      const cartId = cart.id
      const checksum = cart.checksum
      const data = await checkout({ cartId, checksum, ...options })
      return data
    } catch (error) {
      throw error
    }
  }

  async function handleCreateCart(options) {
    try {
      const data = await createCart(options)
      const cart = _.get(data, 'cart', {})
      setDineInCartId(_.get(cart, 'id', ''))
      setInventoryStoreCode(_.get(cart, 'cartShipments.0.inventoryStore.code', ''))
      setInventoryStoreId(_.get(cart, 'cartShipments.0.inventoryStore.id', null))
      setCart(cart)
      setTotalItems(getMainLineProperties(cart.cartLineProperties))
      setInitCart(cart)
      return data
    } catch (error) {
      throw error
    }
  }

  async function handleFetchCart(options) {
    try {
      const data = await fetchCart({ cartId: dineInCartId, ...options })
      const cart = _.get(data, 'cart', {})
      setCart(cart)
      setTotalItems(getMainLineProperties(cart.cartLineProperties))
      setInitCart(cart)
      setInventoryStoreCode(_.get(cart, 'cartShipments.0.inventoryStore.code', ''))
      setInventoryStoreId(_.get(cart, 'cartShipments.0.inventoryStore.id', null))

      return data
    } catch (error) {
      throw error
    }
  }

  async function handleUpdateCart(options) {
    try {
      const data = await updateCart({ cartId: dineInCartId, ...options })
      const cart = _.get(data, 'cart', {})
      setCart(cart)
      setTotalItems(getMainLineProperties(cart.cartLineProperties))
      setInitCart(cart)
      setInventoryStoreCode(_.get(cart, 'cartShipments.0.inventoryStore.code', ''))
      setInventoryStoreId(_.get(cart, 'cartShipments.0.inventoryStore.id', null))

      return data
    } catch (error) {
      // createCartIfNotExist(error)
      throw error
    }
  }

  function handleUpdateStagingCart(currentStagingCart) {
    setStagingCart((prevStagingCart) => {
      const updatedActions = mergeCartActions(
        _.get(prevStagingCart, 'actions', []),
        _.get(currentStagingCart, 'actions', [])
      )
      const totalItems = getMainLineProperties(
        _.map(updatedActions, (action) => ({
          ...action,
          isMainProperty: _.isNull(_.get(action, 'productAddonId', null))
        }))
      )
      return {
        ...prevStagingCart,
        actions: updatedActions,
        totalItems
      }
    })
  }

  const totalStagingItems = useMemo(() => (
    _.get(stagingCart, 'totalItems', 0)
  ), [_.get(stagingCart, 'totalItems', 0)])

  const contextValue = {
    cart,
    checkout: handleCheckout,
    createCart: handleCreateCart,
    dineInCartId,
    fetchCart: handleFetchCart,
    initCart,
    initCartForCartBadge: handleInitCartForCartBadge,
    inventoryStoreCode,
    inventoryStoreId,
    refreshCart: handleRefreshCart,
    resetCart: handleResetCart,
    resetStagingCart: handleResetStagingCart,
    stagingCart,
    totalItems,
    totalStagingItems,
    updateCart: handleUpdateCart,
    updateStagingCart: handleUpdateStagingCart
  }
  return (
    <DineInCartContext.Provider value={contextValue}>
      {children}
    </DineInCartContext.Provider>
  )
}
